/**
 * ReschedulePage — public /reschedule/:token route (iter113).
 *
 * Flow:
 *   1) Fetch GET /api/demo/bookings/reschedule/{token} — returns booking
 *      summary + slot grid for the prospect's current timezone.
 *   2) Show current slot + timezone selector + new-slot picker.
 *   3) POST /api/demo/bookings/reschedule/{token} with the new slot.
 *   4) Success screen with the new time and inbox reminder.
 *
 * Handles 401 (bad token) and 410 (expired) with dedicated states so the
 * prospect knows exactly what happened. Reuses LandingNav + the landing
 * footer for visual consistency with the /contact page.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Calendar, Clock, Check, Loader2, Globe, AlertTriangle, ArrowRight,
} from 'lucide-react';
import LandingNav from '@/components/landing/LandingNav';
import BookDemoModal from '@/components/landing/BookDemoModal';
import DotboxLogo from '@/components/common/DotboxLogo';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  { value: 'America/New_York',   label: 'New York (Eastern · ET)' },
  { value: 'America/Chicago',    label: 'Chicago (Central · CT)' },
  { value: 'America/Denver',     label: 'Denver (Mountain · MT)' },
  { value: 'America/Phoenix',    label: 'Phoenix (Arizona · MST)' },
  { value: 'America/Los_Angeles',label: 'Los Angeles (Pacific · PT)' },
  { value: 'America/Toronto',    label: 'Toronto (ET)' },
  { value: 'America/Sao_Paulo',  label: 'São Paulo (BRT)' },
  { value: 'Europe/London',      label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',       label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin',      label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam',   label: 'Amsterdam (CET/CEST)' },
  { value: 'Africa/Johannesburg',label: 'Johannesburg (SAST)' },
  { value: 'Asia/Dubai',         label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata',       label: 'Mumbai / Delhi (IST)' },
  { value: 'Asia/Singapore',     label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong',     label: 'Hong Kong (HKT)' },
  { value: 'Asia/Tokyo',         label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney',   label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland',   label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC',                label: 'UTC (Coordinated Universal Time)' },
];

const CenterShell = ({ children }) => (
  <div className="min-h-screen bg-[#0A1024] text-slate-200 flex flex-col">
    <LandingNav onOpenBookDemo={() => {}} />
    <main className="flex-1 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </main>
    <footer className="bg-[#0A1024] border-t border-[#1F2A4D] py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <DotboxLogo variant="dark" className="h-8" />
        <div className="flex items-center gap-3">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}
            className="text-slate-400 hover:text-rose-400 text-sm"
            data-testid="reschedule-footer-home"
          >← Back to home</Link>
          <span className="text-slate-600">·</span>
          <p className="text-slate-500 text-sm">©2026 dotbox.ai — All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
);

const ReschedulePage = () => {
  const { token } = useParams();
  const [showBookDemo, setShowBookDemo] = useState(false);

  const [state, setState] = useState('loading'); // loading | ready | expired | bad | notfound | success | error
  const [booking, setBooking] = useState(null);
  const [slotsPayload, setSlotsPayload] = useState(null);
  const [tz, setTz] = useState('America/New_York');
  const [selectedDateIso, setSelectedDateIso] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const fetchState = useCallback(async (targetTz) => {
    try {
      const params = targetTz ? { tz: targetTz } : undefined;
      const r = await axios.get(`${API_BASE}/demo/bookings/reschedule/${token}`, { params });
      setBooking(r.data.booking);
      setSlotsPayload(r.data.slots);
      if (!targetTz) setTz(r.data.booking.timezone || 'America/New_York');
      const first = (r.data.slots?.days || []).find((d) => d.slots.some((s) => s.available));
      setSelectedDateIso(first?.local_date_iso || null);
      setSelectedSlot(null);
      setState('ready');
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) setState('expired');
      else if (code === 401) setState('bad');
      else if (code === 404) setState('notfound');
      else setState('error');
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setState('bad'); return; }
    fetchState();
  }, [token, fetchState]);

  // Re-fetch slot grid when the prospect changes tz (booking summary stays)
  useEffect(() => {
    if (state !== 'ready' || !tz || !booking) return;
    if (tz === slotsPayload?.timezone) return;
    fetchState(tz);
  }, [tz, state, booking, slotsPayload, fetchState]);

  const selectedDay = useMemo(
    () => (slotsPayload?.days || []).find((d) => d.local_date_iso === selectedDateIso) || null,
    [slotsPayload, selectedDateIso]
  );

  const canConfirm = !!selectedSlot && !submitting;

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/demo/bookings/reschedule/${token}`, {
        slot_start_utc: selectedSlot.start_utc,
        timezone: tz,
      });
      setConfirmation(r.data);
      setState('success');
      toast.success('Your demo has been rescheduled — updated invite is on its way.');
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 409) { toast.error('That slot was just taken. Pick another.'); fetchState(tz); }
      else if (code === 410) setState('expired');
      else if (code === 401) setState('bad');
      else toast.error(typeof detail === 'string' ? detail : 'Reschedule failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Error / boundary states ----
  if (state === 'loading') {
    return (
      <CenterShell>
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center" data-testid="reschedule-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifying your reschedule link…
        </div>
      </CenterShell>
    );
  }
  if (state === 'expired' || state === 'bad' || state === 'notfound' || state === 'error') {
    const isExpired = state === 'expired';
    const isNotFound = state === 'notfound';
    return (
      <CenterShell>
        <Card className="border border-amber-500/40" data-testid={`reschedule-${state}`}>
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <h1 className="text-lg font-semibold text-white">
                {isExpired
                  ? "This reschedule link has expired."
                  : isNotFound
                    ? "We couldn't find that booking."
                    : "We couldn't verify that reschedule link."}
              </h1>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6">
              {isExpired
                ? "Reschedule links are valid until one hour before the meeting. Please book a fresh demo below — we'll be happy to line up a new time."
                : "The link may be malformed, revoked, or the booking may already have been cancelled. Book a fresh demo below — we'll get you scheduled in a couple of clicks."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setShowBookDemo(true)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="reschedule-fallback-book"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a new demo
              </Button>
              <Button asChild variant="outline" className="border-[#1F2A4D] bg-[#0A1024] text-slate-200">
                <Link to="/contact">Contact us instead</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
        <BookDemoModal open={showBookDemo} onOpenChange={setShowBookDemo} />
      </CenterShell>
    );
  }

  // ---- Success state ----
  if (state === 'success' && confirmation) {
    return (
      <CenterShell>
        <Card className="border border-emerald-500/40" data-testid="reschedule-success">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center py-4">
              <div className="h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
                <Check className="h-7 w-7 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-2">Rescheduled — you&apos;re all set.</h1>
              <p className="text-sm text-slate-400 max-w-md">
                We&apos;ve emailed an updated calendar invite. Just open the new .ics attachment to replace the old event.
              </p>
            </div>
            <div className="bg-[#0A1024] border border-[#1F2A4D] rounded-md p-4 space-y-2 mt-6">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-red-400" />
                <span className="text-slate-100 font-medium">{confirmation.local_start_display}</span>
                <span className="text-slate-500">→ {confirmation.local_end_display}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Globe className="h-3.5 w-3.5" /> {confirmation.timezone}
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button asChild className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
                <Link to="/">Done</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </CenterShell>
    );
  }

  // ---- Ready — full picker ----
  return (
    <CenterShell>
      <div className="mb-6" data-testid="reschedule-page">
        <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-2">Reschedule</div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Hi {booking?.full_name_first || 'there'} — let&apos;s pick a new time.
        </h1>
        <p className="text-sm text-slate-400">
          Your current time is shown below. Choose a new slot and we&apos;ll email a fresh calendar invite.
        </p>
      </div>

      {/* Current slot */}
      <Card className="border mb-6" data-testid="reschedule-current-slot">
        <CardContent className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Current time</div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-red-400" />
            <span className="text-slate-100 font-medium">{booking?.current_local_start_display}</span>
            <span className="text-slate-500">→ {booking?.current_local_end_display}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">{booking?.timezone}</div>
          {(booking?.reschedule_count || 0) > 0 && (
            <Badge variant="outline" className="mt-3 border-amber-500/40 text-amber-300 bg-amber-950/20 text-[10px]">
              Rescheduled {booking.reschedule_count}× so far
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Picker */}
      <Card className="border">
        <CardContent className="p-6 space-y-6">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Globe className="h-3 w-3" /> Timezone
            </Label>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="w-full bg-[#0A1024] border border-[#1F2A4D] rounded-md px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-red-500"
              data-testid="reschedule-timezone"
            >
              {TIMEZONES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider mb-2 block">1 — Select a date</Label>
            {(slotsPayload?.days || []).length === 0 ? (
              <div className="text-sm text-slate-400" data-testid="reschedule-no-slots">
                No open slots in the next {slotsPayload?.min_lead_days || 2}+ business days. Try another timezone.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="reschedule-dates">
                {slotsPayload.days.map((d) => {
                  const active = d.local_date_iso === selectedDateIso;
                  return (
                    <button
                      key={d.local_date_iso}
                      type="button"
                      onClick={() => { setSelectedDateIso(d.local_date_iso); setSelectedSlot(null); }}
                      className={`px-3 py-2 rounded-md text-sm border transition ${
                        active
                          ? 'bg-red-600/20 border-red-500 text-white'
                          : 'bg-[#0A1024] border-[#1F2A4D] text-slate-200 hover:border-red-600/40'
                      }`}
                      data-testid={`reschedule-date-${d.local_date_iso}`}
                    >
                      {d.local_date_display}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selectedDay && (
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider mb-2 block">
                2 — Select a new time
              </Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="reschedule-slots">
                {selectedDay.slots.map((s) => {
                  const active = selectedSlot?.start_utc === s.start_utc;
                  return (
                    <button
                      key={s.start_utc}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setSelectedSlot(s)}
                      className={`px-3 py-2 rounded-md text-xs font-medium border transition ${
                        active
                          ? 'bg-red-600 border-red-500 text-white'
                          : s.available
                            ? 'bg-[#0A1024] border-[#1F2A4D] text-slate-200 hover:border-red-600/40'
                            : 'bg-[#0A1024]/50 border-[#1F2A4D] text-slate-600 line-through cursor-not-allowed'
                      }`}
                      data-testid={`reschedule-slot-${s.start_utc}`}
                    >
                      {s.local_time_display}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={submit}
              disabled={!canConfirm}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              data-testid="reschedule-confirm"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rescheduling…</>
              ) : (
                <>Confirm new time <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </CenterShell>
  );
};

export default ReschedulePage;
